import { logError } from '@core/logging';
import { Language, languages } from '@core/shared';
import { AccountProfile } from '@core/swagger-client';
import { getTranslations, useFileDropzone } from '@core/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { serializeError } from 'serialize-error';

export interface SpreadsheetField {
    /** Key on AccountProfile this column maps to. */
    name: keyof AccountProfile;
    /** Translation label key under `Account.Label.*` used to auto-detect the column. */
    labelField: string;
    /** Whether this field is mandatory for the import to proceed. */
    required?: boolean;
    /** Extra raw aliases (already lower-cased) for auto-detection. */
    extraAliases?: string[];
}

interface UseSpreadsheetImportResult {
    getRootProps: ReturnType<typeof useFileDropzone>['getRootProps'];
    getInputProps: ReturnType<typeof useFileDropzone>['getInputProps'];
    isDragActive: boolean;
    /** Suffix under `Generic.FileRejection.*` when the last drop was refused. */
    rejectionCode?: string;

    workBookError?: string;
    setWorkBookError: (value: string | undefined) => void;

    workSheetNames: string[];
    selectedWorkSheet: string | null;
    setSelectedWorkSheet: (value: string | null) => void;

    hasHeader: boolean;
    setHasHeader: (value: boolean) => void;

    headers: string[];
    mapping: string[];
    setMapping: (value: string[]) => void;
    data: string[][];

    /** The decorated fields (with translated labels) for rendering the mapping selects. */
    fields: { name: keyof AccountProfile; label: string }[];

    /**
     * Builds the AccountProfile rows from the current mapping. Returns null and sets a
     * work-book error when a required field is not mapped.
     */
    buildProfiles: () => AccountProfile[] | null;
}

/**
 * Shared spreadsheet parsing + column mapping logic used by the various import flows
 * (account invites, webinar invites, e-mail lists). Parameterised by the AccountProfile
 * field list so each caller controls which columns are accepted/required.
 */
export const useSpreadsheetImport = (fieldDefs: SpreadsheetField[]): UseSpreadsheetImportResult => {
    const { t } = useTranslation();

    const [workBookError, setWorkBookError] = useState<string>();
    const [workSheetNames, setWorkSheetNames] = useState<string[]>([]);
    const [workSheets, setWorkSheets] = useState<{ [index: string]: string[][] }>({});
    const [selectedWorkSheet, setSelectedWorkSheet] = useState<string | null>(null);
    const [mapping, setMapping] = useState<string[]>([]);
    const [hasHeader, setHasHeader] = useState<boolean>(true);
    const [headers, setHeaders] = useState<string[]>([]);
    const [data, setData] = useState<string[][]>([]);

    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }
            import('xlsx').then((xlsx) => {
                acceptedFiles[0].arrayBuffer().then((buffer) => {
                    try {
                        const workBook = xlsx.read(buffer, { type: 'array', dense: true, WTF: true });

                        const sheets: { [index: string]: string[][] } = {};
                        for (const sheetName of workBook.SheetNames) {
                            const sheet = workBook.Sheets[sheetName];
                            if (!sheet['!ref'] || !sheet['!data']) {
                                continue;
                            }
                            const range = xlsx.utils.decode_range(sheet['!ref']);

                            const sheetData: string[][] = [];
                            for (let row = range.s.r; row <= range.e.r; row++) {
                                const rowData: string[] = [];
                                for (let col = range.s.c; col <= range.e.c; col++) {
                                    if (!sheet['!data'][row] || !sheet['!data'][row][col]) {
                                        rowData.push('');
                                        continue;
                                    }

                                    const v = sheet['!data'][row][col].v;
                                    if (v === undefined || v === null) {
                                        rowData.push('');
                                        continue;
                                    }

                                    rowData.push(v.toString());
                                }
                                if (rowData.every((str) => !str?.trim())) {
                                    continue;
                                }
                                sheetData.push(rowData);
                            }

                            sheets[sheetName] = sheetData;
                        }

                        setWorkSheetNames(workBook.SheetNames);
                        setWorkSheets(sheets);
                        setWorkBookError(undefined);
                    } catch (error) {
                        logError('Error reading workbook for spreadsheet import', { Error: serializeError(error) });

                        setWorkSheetNames([]);
                        setWorkSheets({});
                        setWorkBookError(serializeError(error).message);
                    }
                });
            });
        },
        accept: {
            'application/vnd.ms-excel': ['.xls', '.xlsx', '.csv', '.ods', '.fods', '.txt']
        }
    });

    const getAlias = (labelField: string, lang: Language): string[] => {
        const key = `Account.Label.${labelField}`;
        const { original, patch } = getTranslations(lang);

        const labels: string[] = [];
        if (original[key]) {
            labels.push(original[key].toLocaleLowerCase());
        }
        if (patch[key]) {
            labels.push(patch[key].toLocaleLowerCase());
        }
        return labels;
    };

    const getAliases = (labelField: string): string[] => {
        let aliases: string[] = [];
        for (const lng in languages) {
            aliases = [...aliases, ...getAlias(labelField, languages[lng].code)];
        }
        return aliases;
    };

    const fieldsWithAliases = fieldDefs.map((field) => ({
        name: field.name,
        label: t(`Account.Label.${field.labelField}`),
        required: field.required ?? false,
        aliases: [...(field.extraAliases ?? []), ...getAliases(field.labelField)]
    }));

    const fields = fieldsWithAliases.map((field) => ({ name: field.name, label: field.label }));

    useEffect(() => {
        let sheetName = selectedWorkSheet || '';
        if (!sheetName || !workSheetNames.includes(sheetName)) {
            if (workSheetNames.length > 0) {
                setSelectedWorkSheet(workSheetNames[0]);
                return;
            }

            setSelectedWorkSheet(null);
            setData([]);
            setHeaders([]);
            setMapping([]);
            return;
        }

        const sheetData = workSheets[sheetName];
        if (sheetData.length > 0) {
            let h: string[] = [];
            if (hasHeader) {
                h = sheetData[0];
            } else {
                for (let i = 0; i < sheetData[0].length; i++) {
                    h.push(`Column ${i + 1}`);
                }
            }
            setHeaders(h);

            const m: string[] = [];
            for (let i = 0; i < h.length; i++) {
                const field = fieldsWithAliases.find((f) => {
                    if (f.label.toLocaleLowerCase() === h[i]?.trim()?.toLocaleLowerCase()) return true;
                    return f.aliases.includes(h[i]?.trim()?.toLocaleLowerCase());
                });
                m.push(field ? field.name : '');
            }
            setMapping(m);
            setData(hasHeader ? sheetData.slice(1) : sheetData);
        } else {
            setData([]);
            setHeaders([]);
            setMapping([]);
        }
    }, [workSheetNames, workSheets, selectedWorkSheet, hasHeader]);

    const buildProfiles = (): AccountProfile[] | null => {
        const missingRequired = fieldsWithAliases.find((f) => f.required && !mapping.includes(f.name));
        if (missingRequired) {
            setWorkBookError(t('ImportInvites.ErrorMessage.WorkBookError'));
            setTimeout(() => setWorkBookError(undefined), 5000);
            return null;
        }

        const profiles = data.map((row) => {
            const profile: Partial<AccountProfile> = {};
            for (let i = 0; i < mapping.length; i++) {
                if (mapping[i]) {
                    profile[mapping[i] as keyof AccountProfile] = row[i];
                }
            }
            return profile as AccountProfile;
        });

        return profiles;
    };

    return {
        getRootProps,
        getInputProps,
        isDragActive,
        rejectionCode,
        workBookError,
        setWorkBookError,
        workSheetNames,
        selectedWorkSheet,
        setSelectedWorkSheet,
        hasHeader,
        setHasHeader,
        headers,
        mapping,
        setMapping,
        data,
        fields,
        buildProfiles
    };
};
